import AWS from 'aws-sdk';

// Configure AWS SDK
// Uses environment variables for credentials - must be set in .env.local
const configureAWS = () => {
  const identityPoolId = process.env.REACT_APP_IDENTITY_POOL_ID;
  const region = process.env.REACT_APP_AWS_REGION || 'us-east-1';

  // Validate that required credentials are configured
  if (!identityPoolId) {
    console.warn('[DEBUG awsService] REACT_APP_IDENTITY_POOL_ID environment variable is not set. Gallery will use mock data.');
  }

  console.log('[DEBUG awsService] Configuring AWS with region:', region);

  AWS.config.update({
    region: region,
    credentials: new AWS.CognitoIdentityCredentials({
      IdentityPoolId: identityPoolId || ''
    })
  });
};

// Initialize S3 client
const getS3Client = () => {
  configureAWS();
  return new AWS.S3();
};

/**
 * List all images in the gallery directory of the S3 bucket
 * @returns {Promise<Array>} Array of image objects with keys and URLs
 */
export const listGalleryImages = async () => {
  const s3 = getS3Client();

  const bucketName = process.env.REACT_APP_S3_BUCKET_NAME;

  // Validate that bucket name is configured
  if (!bucketName) {
    console.warn('[DEBUG awsService.listGalleryImages] REACT_APP_S3_BUCKET_NAME environment variable is not set. Using mock data.');
    return getMockGalleryImages();
  }

  console.log('[DEBUG awsService.listGalleryImages] Fetching images from bucket:', bucketName);

  const params = {
    Bucket: bucketName,
    Prefix: 'gallery/' // The directory in your bucket where gallery images are stored
  };
  
  try {
    const data = await s3.listObjectsV2(params).promise();
    
    // Filter out directory objects and process only image files
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp'];
    
    console.log('S3 listObjectsV2 response:', data);
    
    const images = data.Contents
      .filter(item => {
        const key = item.Key.toLowerCase();
        return imageExtensions.some(ext => key.endsWith(ext));
      })
      .map(item => {
        const url = `https://s3.amazonaws.com/${params.Bucket}/${item.Key}`;
        console.log(`Generated image URL: ${url}`);
        return {
          key: item.Key,
          // Use path-style URL format for buckets with dots in the name (like domain names)
          // This avoids certificate errors with virtual-hosted style URLs
          url: url,
          lastModified: item.LastModified,
          size: item.Size,
          // Extract filename from the key
          name: item.Key.split('/').pop().split('.')[0]
        };
      });
    
    // If no images were found, return mock data for demonstration purposes
    if (images.length === 0) {
      console.log('No images found in S3 bucket, using mock data instead');
      return getMockGalleryImages();
    }
    
    return images;
  } catch (error) {
    console.error('Error fetching images from S3:', error);
    
    // For development/demo purposes, return mock data when S3 is not configured
    // or when there's an error accessing the bucket
    console.log('Using mock gallery data due to S3 error');
    return getMockGalleryImages();
  }
};

/**
 * Get mock gallery images for development/demo purposes
 * @returns {Array} Array of mock image objects
 */
const getMockGalleryImages = () => {
  console.log('Using mock gallery images');
  const mockImages = [
    {
      key: 'gallery/project1.jpg',
      url: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1200&q=80',
      lastModified: new Date(),
      size: 1024000,
      name: 'Modern Family Home'
    },
    {
      key: 'gallery/project2.jpg',
      url: 'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1200&q=80',
      lastModified: new Date(),
      size: 1024000,
      name: 'Luxury Villa'
    },
    {
      key: 'gallery/project3.jpg',
      url: 'https://images.unsplash.com/photo-1558036117-15d82a90b9b1?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1200&q=80',
      lastModified: new Date(),
      size: 1024000,
      name: 'Home Renovation'
    },
    {
      key: 'gallery/project4.jpg',
      url: 'https://images.unsplash.com/photo-1613545325278-f24b0cae1224?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1200&q=80',
      lastModified: new Date(),
      size: 1024000,
      name: 'Interior Design'
    },
    {
      key: 'gallery/project5.jpg',
      url: 'https://images.unsplash.com/photo-1531834685032-c34bf0d84c77?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1200&q=80',
      lastModified: new Date(),
      size: 1024000,
      name: 'Kitchen Remodel'
    },
    {
      key: 'gallery/project6.jpg',
      url: 'https://images.unsplash.com/photo-1582268611958-ebfd161ef9cf?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1200&q=80',
      lastModified: new Date(),
      size: 1024000,
      name: 'Bathroom Renovation'
    }
  ];
  console.log('Mock images:', mockImages);
    return mockImages;
  };
  
  // Create a named object before exporting as default
  const awsService = {
    listGalleryImages
  };
  
  export default awsService;